import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  inject,
  signal,
  computed,
  SimpleChanges,
  ViewChild,
  DestroyRef,
  HostListener,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LabelService } from '@shared/services/label.service';
import { SnackBarService } from '@shared-services';
import { Label, LabelDefinition, ObjectLabel } from '@shared/models/label.model';
import { LabelChipComponent } from '@shared/components/label-chip/label-chip.component';
import { CmUiStandardButtonComponent } from '@cm/ui-modules';
import { switchMap, forkJoin } from 'rxjs';

@Component({
  selector: 'app-label-button',
  templateUrl: './label-button.component.html',
  styleUrls: ['./label-button.component.scss'],
  standalone: true,
  imports: [
    CmUiStandardButtonComponent,
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTooltipModule,
    TranslateModule,
    LabelChipComponent,
  ],
})
export class LabelButtonComponent implements OnInit, OnChanges {
  @Input({ required: true }) objectType!: string;
  @Input() objectUuid!: string;
  /**
   * Labels already attached to the object, as delivered by the list response. When provided the
   * button does not fetch them itself (one request per row otherwise); the parent list reloads on change.
   */
  @Input() labels?: Label[] | null;
  /** How many chips the trigger shows before collapsing the rest into a "+N" count. */
  @Input() maxVisible = 3;
  /** Filter mode: pass selected label UUIDs from outside */
  @Input() filterMode = false;
  @Input() filterSelectedUuids: string[] = [];
  @Output() filterChange = new EventEmitter<string[]>();

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  private readonly labelService = inject(LabelService);
  private readonly snackBar = inject(SnackBarService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  definitions = signal<LabelDefinition[]>([]);
  attachedLabels = signal<ObjectLabel[]>([]);
  loading = signal(false);

  // System-managed definitions (e.g. "Compliance", written solely by
  // ComplianceStatusService) are never manually togglable — exclude them
  // from both the menu and the attached-labels summary on the trigger button.
  sortedAttachedLabels = computed(() => {
    const labels = this.attachedLabels().filter((ol) => !ol.label.labelDefinition?.systemManaged);
    const isColor = (ol: ObjectLabel) =>
      (ol.label.renderType ?? ol.label.labelDefinition?.renderType) === 'COLOR';
    return [...labels.filter((ol) => !isColor(ol)), ...labels.filter((ol) => isColor(ol))];
  });

  sortedDefinitions = computed(() => {
    const defs = this.definitions().filter((d) => !d.systemManaged);
    const tags = defs.filter((d) => d.renderType !== 'COLOR');
    const colors = defs.filter((d) => d.renderType === 'COLOR');
    return [...tags, ...colors];
  });

  ngOnInit(): void {
    this.loadDefinitions();
    if (!this.filterMode) {
      if (this.labels) {
        this.applyProvidedLabels();
      } else {
        this.loadAttached();
        this.labelService.refresh$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.loadAttached());
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.filterMode) return;
    if (this.labels) {
      if (changes['labels']) this.applyProvidedLabels();
    } else if (changes['objectUuid'] && !changes['objectUuid'].firstChange) {
      this.loadAttached();
    }
  }

  private applyProvidedLabels(): void {
    this.attachedLabels.set(
      (this.labels ?? []).map((label) => ({
        id: label.id,
        uuid: label.objectLabelUuid ?? '',
        relatedObjectType: this.objectType,
        relatedObjectUuid: this.objectUuid,
        createdDate: label.createdDate,
        ownerUuid: '',
        label,
      }))
    );
  }

  private loadDefinitions(): void {
    this.labelService.getLabelDefinitions().subscribe({
      next: (data) => this.definitions.set(data),
    });
  }

  /** After a change: refetch (self-loading mode) or patch locally (labels provided by the list). */
  private afterChange(update: (current: ObjectLabel[]) => ObjectLabel[]): void {
    if (this.labels) {
      this.attachedLabels.update(update);
    } else {
      this.loadAttached();
    }
  }

  loadAttached(): void {
    if (!this.objectUuid) return;
    this.labelService.getLabelsForObject(this.objectType, this.objectUuid).subscribe({
      next: (data) => this.attachedLabels.set(data),
      error: () => this.attachedLabels.set([]),
    });
  }

  isAttached(labelUuid: string): boolean {
    return this.attachedLabels().some((ol) => ol.label.uuid === labelUuid);
  }

  private getObjectLabelUuid(labelUuid: string): string | undefined {
    return this.attachedLabels().find((ol) => ol.label.uuid === labelUuid)?.uuid;
  }

  toggleAndClose(label: Label): void {
    this.toggle(label);
    this.menuTrigger?.closeMenu();
  }

  attachedCountFor(definition: LabelDefinition): number {
    const labelUuids = new Set((definition.labels || []).map((l) => l.uuid));
    return this.attachedLabels().filter((ol) => labelUuids.has(ol.label.uuid)).length;
  }

  toggle(label: Label): void {
    if (this.isAttached(label.uuid)) {
      this.detach(label);
    } else {
      this.attachWithSelectionEnforcement(label);
    }
  }

  /** For SINGLE definitions, detach ALL existing values before attaching new one. */
  private attachWithSelectionEnforcement(label: Label): void {
    const definition = label.labelDefinition;
    if (definition?.selectionType === 'SINGLE') {
      const existing = this.attachedLabels().filter(
        (ol) => ol.label.labelDefinition?.uuid === definition.uuid
      );
      if (existing.length > 0) {
        this.loading.set(true);
        forkJoin(existing.map((ol) => this.labelService.detachLabel(ol.uuid))).pipe(
          switchMap(() => this.labelService.attachLabel({
            relatedObjectType: this.objectType,
            relatedObjectUuid: this.objectUuid,
            labelUuid: label.uuid,
          }))
        ).subscribe({
          next: (created) => {
            this.afterChange((current) => [
              ...current.filter((ol) => ol.label.labelDefinition?.uuid !== definition.uuid),
              created,
            ]);
            this.loading.set(false);
          },
          error: () => {
            this.snackBar.showWithText(this.translate.instant('ADMIN.labels.button.attachError'), 'fail');
            this.loading.set(false);
          },
        });
        return;
      }
    }
    this.attach(label);
  }

  private attach(label: Label): void {
    this.loading.set(true);
    this.labelService
      .attachLabel({
        relatedObjectType: this.objectType,
        relatedObjectUuid: this.objectUuid,
        labelUuid: label.uuid,
      })
      .subscribe({
        next: (created) => { this.afterChange((current) => [...current, created]); this.loading.set(false); },
        error: () => {
          this.snackBar.showWithText(this.translate.instant('ADMIN.labels.button.attachError'), 'fail');
          this.loading.set(false);
        },
      });
  }

  private detach(label: Label): void {
    const objectLabelUuid = this.getObjectLabelUuid(label.uuid);
    if (!objectLabelUuid) return;
    this.loading.set(true);
    this.labelService.detachLabel(objectLabelUuid).subscribe({
      next: () => {
        this.afterChange((current) => current.filter((ol) => ol.uuid !== objectLabelUuid));
        this.loading.set(false);
      },
      error: () => {
        this.snackBar.showWithText(this.translate.instant('ADMIN.labels.button.detachError'), 'fail');
        this.loading.set(false);
      },
    });
  }

  // --- Filter mode methods ---

  isFilterSelected(labelUuid: string): boolean {
    return this.filterSelectedUuids.includes(labelUuid);
  }

  toggleFilterLabel(label: Label): void {
    const current = [...this.filterSelectedUuids];
    const idx = current.indexOf(label.uuid);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(label.uuid);
    }
    this.filterChange.emit(current);
    this.menuTrigger?.closeMenu();
  }

  clearFilterSelection(): void {
    this.filterChange.emit([]);
    this.menuTrigger?.closeMenu();
  }

  filterCountFor(definition: LabelDefinition): number {
    const labelUuids = new Set((definition.labels || []).map((l) => l.uuid));
    return this.filterSelectedUuids.filter((u) => labelUuids.has(u)).length;
  }

  /** The trigger shows an icon while nothing is selected / attached, the label chips once there are some. */
  get triggerIcon(): string | undefined {
    if (this.filterMode) {
      return this.filterSelectedUuids.length === 0 ? 'filter_list' : undefined;
    }
    return this.sortedAttachedLabels().length === 0 ? 'label_off' : undefined;
  }

  /** The button sits in rows and cards that open on click — opening the menu must not open the row. */
  @HostListener('click', ['$event'])
  protected stopRowClick(event: Event): void {
    event.stopPropagation();
  }

  // Returns label objects for currently selected filter UUIDs (for chip display)
  get selectedFilterLabels(): Label[] {
    const result: Label[] = [];
    for (const def of this.definitions()) {
      for (const label of def.labels || []) {
        if (this.filterSelectedUuids.includes(label.uuid)) {
          result.push(label);
        }
      }
    }
    return result;
  }

  definitionIcon(definition: LabelDefinition): string {
    return definition.icon || 'label';
  }

  labelStyle(label: Label): { [key: string]: string } {
    if (label.color) {
      return {
        'background-color': label.color,
        'color': label.textColor || '#ffffff',
      };
    }
    return {};
  }
}
